
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Rating() {
  const navigate = useNavigate();
  const [serviceRating, setServiceRating] = useState(0);
  const [technicianRating, setTechnicianRating] = useState(0);
  const [comment, setComment] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages = Array.from(files).map(file => URL.createObjectURL(file));
      setImages([...images, ...newImages]);
    }
  };

  const handleSubmit = () => {
    setShowSuccess(true);
    setTimeout(() => {
      navigate('/orders');
    }, 2000);
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-[#F6F6F6] flex items-center justify-center px-6">
        <div className="text-center animate-fadeIn">
          <div className="w-24 h-24 bg-[#FBC02D] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-soft">
            <i className="ri-star-fill text-white text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-[#212121] mb-2">شكراً لتقييمك!</h2>
          <p className="text-gray-600">نقدر رأيك ونسعى دائماً لتحسين خدماتنا</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F6F6] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1565C0] to-[#2E7D32] pt-12 pb-8 px-6 rounded-b-[30px]">
        <div className="flex items-center gap-3 mb-6 animate-fadeIn">
          <button onClick={() => navigate('/orders')} className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <i className="ri-arrow-right-line text-white text-lg"></i>
          </button>
          <h1 className="text-xl font-bold text-white">تقييم الخدمة</h1>
        </div>

        {/* Technician Card */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 animate-slideUp">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/fb9f3c4720c66a4e5914734b1fd15792.jpg"
                alt="الفني"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white">أحمد محمد</h3>
              <p className="text-white/80 text-sm">تمريض منزلي</p>
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-lg">207</div>
              <div className="text-white/80 text-xs">ر.س</div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Rating */}
      <div className="px-6 mt-6">
        <div className="bg-white rounded-2xl p-5 shadow-md animate-slideUp">
          <h3 className="font-bold text-[#212121] mb-3 text-center">كيف كانت جودة الخدمة؟</h3>
          
          <div className="flex justify-center gap-2 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setServiceRating(star)}
                className="transition-all active:scale-110"
              >
                <i
                  className={`${
                    star <= serviceRating ? 'ri-star-fill text-[#FBC02D]' : 'ri-star-line text-gray-300'
                  } text-4xl`}
                ></i>
              </button>
            ))}
          </div>
          
          {serviceRating > 0 && (
            <p className="text-center text-sm text-gray-600 animate-fadeIn">
              {serviceRating === 5 && 'ممتاز! 🌟'}
              {serviceRating === 4 && 'جيد جداً 👍'}
              {serviceRating === 3 && 'جيد 😊'}
              {serviceRating === 2 && 'مقبول 😐'}
              {serviceRating === 1 && 'يحتاج تحسين 😔'}
            </p>
          )}
        </div>
      </div>

      {/* Technician Rating */}
      <div className="px-6 mt-4">
        <div className="bg-white rounded-2xl p-5 shadow-md animate-slideUp stagger-1">
          <h3 className="font-bold text-[#212121] mb-3 text-center">كيف كان أداء الفني؟</h3>
          
          <div className="flex justify-center gap-2 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setTechnicianRating(star)}
                className="transition-all active:scale-110"
              >
                <i
                  className={`${
                    star <= technicianRating ? 'ri-star-fill text-[#FBC02D]' : 'ri-star-line text-gray-300'
                  } text-4xl`}
                ></i>
              </button>
            ))}
          </div>
          
          {technicianRating > 0 && (
            <p className="text-center text-sm text-gray-600 animate-fadeIn">
              {technicianRating === 5 && 'احترافي للغاية! 💯'}
              {technicianRating === 4 && 'محترف 👨‍🔧'}
              {technicianRating === 3 && 'جيد ✓'}
              {technicianRating === 2 && 'مقبول'}
              {technicianRating === 1 && 'يحتاج تدريب'}
            </p>
          )}
        </div>
      </div>

      {/* Comment */}
      <div className="px-6 mt-4">
        <div className="bg-white rounded-2xl p-5 shadow-md animate-slideUp stagger-2">
          <h3 className="font-bold text-[#212121] mb-3 flex items-center gap-2">
            <i className="ri-chat-3-line text-[#1565C0]"></i>
            أخبرنا المزيد (اختياري)
          </h3>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="شاركنا تجربتك مع الخدمة..."
            className="w-full bg-[#F6F6F6] rounded-xl p-3 outline-none text-sm resize-none"
            rows={4}
            maxLength={500}
          />
          <div className="text-left text-xs text-gray-400 mt-1">
            {comment.length}/500
          </div>
        </div>
      </div>

      {/* Upload Images */}
      <div className="px-6 mt-4">
        <div className="bg-white rounded-2xl p-5 shadow-md animate-slideUp stagger-3">
          <h3 className="font-bold text-[#212121] mb-3 flex items-center gap-2">
            <i className="ri-image-line text-[#1565C0]"></i>
            صور بعد التنفيذ (اختياري)
          </h3>
          
          <div className="grid grid-cols-4 gap-2">
            {images.map((img, index) => (
              <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => setImages(images.filter((_, i) => i !== index))}
                  className="absolute top-1 left-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-white text-xs"></i>
                </button>
              </div>
            ))}
            
            {images.length < 4 && (
              <label className="aspect-square bg-[#F6F6F6] rounded-lg flex flex-col items-center justify-center cursor-pointer border-2 border-dashed border-gray-300">
                <i className="ri-add-line text-gray-400 text-2xl"></i>
                <span className="text-xs text-gray-400 mt-1">إضافة</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 shadow-lg">
        <button
          onClick={handleSubmit}
          disabled={serviceRating === 0 || technicianRating === 0}
          className={`w-full py-4 rounded-xl font-bold shadow-lg transition-all ${
            serviceRating === 0 || technicianRating === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-l from-[#1565C0] to-[#2E7D32] text-white hover:shadow-xl active:scale-[0.98]'
          }`}
        >
          إرسال التقييم
        </button>
      </div>
    </div>
  );
}
